import useModuleFilters from '../../../shared/data/useModuleFilters';
import { defineFormSchema } from '../../../shared/data/formSchema';
import { PageFrame, AdaptiveFilterBar } from '../../../shared/components/patterns';
import React from 'react';
import { Clock, Lightbulb, Mail, RefreshCw, Repeat2 } from '../../../shared/assets/AppIcons';
import {
  ActiveAutomationCard,
  AutomationDetailsDialog,
  AutomationStatsGrid,
  AutomationsHeader,
  AutomationsTabs,
  EditAutomationDialog,
  EmptyAutomationsState,
  PauseAutomationDialog,
  RequestAutomationDialog,
  RequestCustomAutomationDialog,
  SuggestedAutomationCard,
} from '@/modules/automations/components';
import {
  useAutomationAnalytics,
  useAutomationRequests,
  useAutomationsOverview,
} from '@/modules/automations/hooks';
import {
  formatAutomationDateTime,
  getAutomationRenewalDate,
} from '@/modules/automations/utils';

const automationFormSchema = defineFormSchema({ id: 'automations.request', fields: [
  { name: 'nome', label: 'Nome' }, { name: 'descrizione', label: 'Descrizione', type: 'textarea' },
  { name: 'funzionalita', label: 'Funzionalità', type: 'textarea' }, { name: 'obiettivo', label: 'Obiettivo', type: 'textarea' },
  { name: 'categoria', label: 'Categoria' }, { name: 'costoMensile', label: 'Costo mensile', type: 'currency' },
  { name: 'ultimoUtilizzo', label: 'Ultimo utilizzo', type: 'date' },
] });

export default function AutomationsPage() {
  const overview = useAutomationsOverview();
  const analytics = useAutomationAnalytics();
  const requests = useAutomationRequests();
  const moduleFilters = useModuleFilters({ schema: automationFormSchema, rows: [...overview.activeAutomations, ...overview.suggestedAutomations] });
  const renewalDate = getAutomationRenewalDate();

  return (
    <PageFrame filterModel={moduleFilters} className="p-8">
      <AutomationsHeader />

      <div className="mb-6 rounded-xl border border-[var(--warning-border)] bg-[var(--warning-soft)] px-4 py-3 text-sm text-[var(--warning-text)]">
        Modulo automazioni in modalita demo locale: dati, statistiche e richieste non sono persistiti su backend.
      </div>

      <AutomationStatsGrid
        activeCount={overview.activeAutomations.length}
        stats={overview.stats}
      />

      <AdaptiveFilterBar />

      <AutomationsTabs
        activeTab={overview.activeTab}
        activeCount={overview.activeAutomations.length}
        suggestedCount={overview.suggestedAutomations.length}
        onTabChange={overview.setActiveTab}
        onRequestCustom={requests.openCustomRequestDialog}
      />

      {overview.activeTab === 'attive' ? (
        <div className="space-y-4">
          {overview.activeAutomations.length > 0 ? (
            moduleFilters.filterRows(overview.activeAutomations).map((automation) => (
              <ActiveAutomationCard
                key={automation.id}
                automation={automation}
                renewalActive={overview.renewalStatus[automation.id]}
                expirationDate={renewalDate}
                formatDateTime={formatAutomationDateTime}
                onOpenDetails={analytics.openDetails}
                onOpenEdit={requests.openEditDialog}
                onOpenPause={requests.openPauseDialog}
                onToggleRenewal={overview.toggleRenewal}
              />
            ))
          ) : (
            <EmptyAutomationsState onDiscover={() => overview.setActiveTab('suggerite')} />
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-[var(--warning-soft)] border border-[var(--warning-border)] rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <Lightbulb className="text-[var(--warning-text)]" size={16} />
              <p className="text-sm text-[var(--warning-text)]">
                <strong>Anteprima locale:</strong> queste automazioni suggerite fanno parte del catalogo demo del modulo.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {moduleFilters.filterRows(overview.suggestedAutomations).map((automation) => (
              <SuggestedAutomationCard
                key={automation.id}
                automation={automation}
                onRequest={requests.openRequestDialog}
              />
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 bg-[var(--surface-raised)] border border-[var(--border)] rounded-lg p-4">
        <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
          <RefreshCw size={16} />
          <span>
            Statistiche e stato visualizzati da seed locale compat. Ultimo refresh UI:{' '}
            {new Date().toLocaleString('it-IT')}
          </span>
        </div>
      </div>

      <AutomationDetailsDialog
        open={analytics.detailsOpen}
        onOpenChange={(open) => (open ? null : analytics.closeDetails())}
        automation={analytics.selectedAutomation}
        timeFilter={analytics.timeFilter}
        onTimeFilterChange={analytics.setTimeFilter}
        timeFilters={analytics.timeFilters}
        periodData={analytics.currentPeriodData}
        userData={analytics.currentUserData}
        totalUsage={analytics.totalUsage}
      />

      <EditAutomationDialog
        open={requests.editDialogOpen}
        onOpenChange={(open) => (open ? null : requests.closeEditDialog())}
        automation={requests.selectedAutomation}
        value={requests.updateRequestText}
        onChange={requests.setUpdateRequestText}
        onSubmit={requests.submitUpdateRequest}
        submitted={requests.updateRequestSent}
      />

      <PauseAutomationDialog
        open={requests.pauseDialogOpen}
        onOpenChange={(open) => (open ? null : requests.closePauseDialog())}
        automation={requests.selectedAutomation}
        expirationDate={renewalDate}
        onConfirm={() => {
          overview.toggleRenewal(requests.selectedAutomation?.id);
          requests.closePauseDialog();
        }}
      />

      <RequestAutomationDialog
        open={requests.requestDialogOpen}
        onOpenChange={(open) => (open ? null : requests.closeRequestDialog())}
        automation={requests.selectedSuggestedAutomation}
        notes={requests.newRequestNotes}
        onNotesChange={requests.setNewRequestNotes}
        onSubmit={requests.submitSuggestedRequest}
        submitted={requests.newRequestSent}
      />

      <RequestCustomAutomationDialog
        open={requests.customRequestDialogOpen}
        onOpenChange={(open) => (open ? null : requests.closeCustomRequestDialog())}
        request={requests.customRequest}
        onFieldChange={requests.updateCustomRequestField}
        onSubmit={requests.submitCustomRequest}
        submitted={requests.customRequestSent}
      />
    </PageFrame>
  );
}
